import { h, Component } from 'preact';
import util from 'preact-util';
import { raceLocalSuffix } from '../../lib/raceTime';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import md5 from 'crypto-js/md5'
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { openUserProfileDrawer } from '../../lib/userProfile';

import FormFieldsCompact from '../../components/form/fieldsCompact';
import Input from '../../components/form/input';
import ImageScroller from '../../components/imagescroller';
import InspectionStatus from '../../components/inspection/status';
import GuiList from '../../components/gui/list';
import PresenceDot from '../../components/gui/presenceDot';
import JuniorBadge from '../../components/raceClassContestant/juniorBadge';
import ReplayControls from './replayControls';
import { has } from 'mobx';

function displayImg(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3MediumLink}`;
    return imgSrc;
}

const fieldSorter = (fields) => (a, b) => fields.map(o => {
    let dir = 1;
    if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
    }
    return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
}).reduce((p, n) => p ? p : n, 0);

// function timeUsed(rcc, sortedCheckpoints) {
//     const startTime = rcc.startTime;
//     let lastTimestamp = startTime;


//     sortedCheckpoints.forEach((cp, idx) => {
//         const rccCp = rcc.checkpoints?.find(e => e.checkpoint === cp.md5) || {};
//         lastTimestamp = rccCp.timestampOut || rccCp.timestampIn;
//     });

//     const tu = util.dateDiff(startTime, lastTimestamp)
//     return tu.seconds;
// }

@withText(props => ({
    titleText: <Text id='race-class-contestants.title'>Race Contestants</Text>,
}))
@observer
class RaceClassContestantList extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            cutoffDate: null,
            // showOfflineResults is now stored in raceClassContestantStore
            // Replay state
            replayMode: false,
            replayPlaying: false,
            replayCurrentTime: null,
            replaySpeed: 100, // Default speed for results replay
            replayStartTime: null,
            replayEndTime: null,
            // Full data for client-side filtering
            fullContestantsData: null,
        };
        this.replayFrameId = null;
        this.lastTickTimestamp = null;
    }

    viewRaceClassContestant = e => {
        const {
            view,
            race,
            raceClass,
            raceClassContestants,
        } = this.props;
        if (view) {
            return view(e);
        }
        const { id, title } = e.target.closest('.box-line').dataset;
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;
        // console.log('viewPart', { id });
        appState.openDrawer('viewRaceClassContestant', {
            id,
            // title,
            height: drawerHeightMedium,
            editFunction: this.editMediaViaView,
            race,
            raceClass,
            raceClassContestants,
            showResults: true,
            callback: () => {
                // appState.toggleDrawer();
                // this.loadAll();
            },
        }, drawerLevel + 1);
    }

    viewRaceClassCheckpoint = e => {
        const {
            race,
            raceClass,
            raceClassContestants,
        } = this.props;

        const { md5 } = e.target.closest('.cp-line').dataset;
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;
        // console.log('viewPart', { id });
        appState.openDrawer('viewRaceClassCheckpoint', {
            md5,
            height: drawerHeightMedium,
            editFunction: this.editMediaViaView,
            race,
            raceClass,
            raceClassContestants,
            callback: () => {
                // appState.toggleDrawer();
                // this.loadAll();
            },
        }, drawerLevel + 1);
    }

    openGeofenceDebug = (e, contestant) => {
        e.stopPropagation();
        const { race, raceClass } = this.props;
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightLarge } = appState;

        appState.openDrawer('viewGeofenceDebug', {
            raceId: race.id,
            contestantId: contestant.id,
            contestant,
            race,
            raceClass,
            height: drawerHeightLarge,
        }, drawerLevel + 1);
    }

    calculateTimeUsed = () => {
        const {
            race,
            raceClass,
            raceClassContestants,
        } = this.props;
        const { raceClassContestantStore } = this.props.stores;

        const sortedCheckpoints = raceClass.checkpoints ? raceClass.checkpoints.sort(fieldSorter(['sortOrder', '-name'])) : [];
        raceClassContestantStore.calculateTimeUsed(sortedCheckpoints, raceClass.id);
    }

    // Replay methods - CLIENT-SIDE FILTERING (no API calls during playback)
    calculateTimeBounds = (contestants) => {
        const { raceClass } = this.props;
        const data = contestants || this.props.raceClassContestants;

        // Start: earliest contestant start time or raceClass.startDate
        const startTimes = data
            .filter(c => c.startTime)
            .map(c => new Date(c.startTime).getTime());
        const replayStartTime = startTimes.length > 0
            ? Math.min(...startTimes)
            : new Date(raceClass.startDate).getTime();

        // End: latest checkpoint timestamp
        const endTimes = data
            .flatMap(c => (c.checkpoints || [])
                .flatMap(cp => [cp.timestampIn, cp.timestampOut].filter(Boolean))
                .map(t => new Date(t).getTime()));
        const replayEndTime = endTimes.length > 0
            ? Math.max(...endTimes)
            : Date.now();

        return { replayStartTime, replayEndTime };
    }

    toggleReplayMode = async () => {
        const { replayMode } = this.state;
        if (!replayMode) {
            // Enter replay mode - load full data ONCE
            const { raceClassContestants } = this.props;

            // Deep clone the contestants data to avoid MobX proxy issues
            const fullContestantsData = JSON.parse(JSON.stringify(raceClassContestants));

            const { replayStartTime, replayEndTime } = this.calculateTimeBounds(fullContestantsData);

            if (replayStartTime && replayEndTime) {
                this.setState({
                    replayMode: true,
                    replayPlaying: false,
                    replayCurrentTime: replayStartTime,
                    replayStartTime,
                    replayEndTime,
                    fullContestantsData,
                });
            }
        } else {
            // Exit replay mode
            this.stopReplayLoop();
            this.setState({
                replayMode: false,
                replayPlaying: false,
                replayCurrentTime: null,
                replayStartTime: null,
                replayEndTime: null,
                fullContestantsData: null,
                cutoffDate: null,
            });
        }
    }

    // Filter contestants data based on current replay time (client-side)
    getFilteredContestants = () => {
        const { replayMode, replayCurrentTime, fullContestantsData } = this.state;
        const { raceClass } = this.props;

        if (!replayMode || !fullContestantsData || !replayCurrentTime) {
            // Filtering for showOnlyAutoResults is done server-side
            return this.props.raceClassContestants;
        }

        const cutoffTime = replayCurrentTime;

        // Filter each contestant's checkpoints based on cutoff time
        return fullContestantsData.map(contestant => {
            const filteredCheckpoints = (contestant.checkpoints || []).map(cp => {
                const cpCopy = { ...cp };

                // Filter out timestamps that are after the cutoff
                if (cpCopy.timestampIn && new Date(cpCopy.timestampIn).getTime() > cutoffTime) {
                    cpCopy.timestampIn = null;
                    cpCopy.positionInToCheckpoint = null;
                }
                if (cpCopy.timestampOut && new Date(cpCopy.timestampOut).getTime() > cutoffTime) {
                    cpCopy.timestampOut = null;
                }
                if (cpCopy.timstampScratch && new Date(cpCopy.timstampScratch).getTime() > cutoffTime) {
                    cpCopy.timstampScratch = null;
                }

                return cpCopy;
            }).filter(cp => cp.timestampIn !== null); // Only keep checkpoints that have been reached

            // Recalculate lastCheckpoint and lastCheckpointLeft
            let lastCheckpoint = 0;
            let lastCheckpointLeft = 0;
            let lastTimestamp = contestant.startTime;

            const sortedCheckpoints = raceClass.checkpoints ? raceClass.checkpoints.sort(fieldSorter(['sortOrder', '-name'])) : [];

            sortedCheckpoints.forEach((cp, idx) => {
                const rccCp = filteredCheckpoints.find(e => e.checkpoint === cp.md5);
                if (rccCp && rccCp.timestampIn) {
                    lastCheckpoint = idx + 1;
                    lastTimestamp = rccCp.timestampIn;
                    if (rccCp.timestampOut) {
                        lastCheckpointLeft = idx + 1;
                        lastTimestamp = rccCp.timestampOut;
                    }
                }
            });

            // Check if contestant has started yet
            const hasStarted = contestant.startTime && new Date(contestant.startTime).getTime() <= cutoffTime;

            return {
                ...contestant,
                checkpoints: filteredCheckpoints,
                lastCheckpoint: hasStarted ? lastCheckpoint : 0,
                lastCheckpointLeft: hasStarted ? lastCheckpointLeft : 0,
                lastTimestamp: hasStarted ? lastTimestamp : null,
                hasScratched: filteredCheckpoints.some(cp => cp.timstampScratch),
                // Hide contestants who haven't started yet
                _hasStarted: hasStarted,
            };
        }).filter(c => c._hasStarted); // Only show contestants who have started (auto filter done server-side)
    }

    startReplayLoop = () => {
        this.lastTickTimestamp = performance.now();
        this.replayFrameId = requestAnimationFrame(this.replayTick);
    }

    stopReplayLoop = () => {
        if (this.replayFrameId) {
            cancelAnimationFrame(this.replayFrameId);
            this.replayFrameId = null;
        }
    }

    replayTick = (timestamp) => {
        const { replayPlaying, replayCurrentTime, replaySpeed, replayEndTime } = this.state;

        if (!replayPlaying) {
            return;
        }

        // Check if we've reached the end
        if (replayCurrentTime >= replayEndTime) {
            this.setState({ replayPlaying: false });
            return;
        }

        // Calculate delta time (in real milliseconds)
        const deltaMs = timestamp - this.lastTickTimestamp;
        this.lastTickTimestamp = timestamp;

        // Advance replay time (real ms * speed multiplier)
        const newTime = replayCurrentTime + (deltaMs * replaySpeed);
        const clampedTime = Math.min(newTime, replayEndTime);

        // Just update the time - NO API CALLS
        // The render will use getFilteredContestants() to filter client-side
        this.setState({
            replayCurrentTime: clampedTime,
        });

        // Continue loop
        this.replayFrameId = requestAnimationFrame(this.replayTick);
    }

    handleReplayPlayPause = () => {
        const { replayPlaying } = this.state;
        if (replayPlaying) {
            this.stopReplayLoop();
            this.setState({ replayPlaying: false });
        } else {
            this.setState({ replayPlaying: true }, () => {
                this.startReplayLoop();
            });
        }
    }

    handleReplaySeek = (newTime) => {
        // Just update the time - NO API CALLS
        this.setState({
            replayCurrentTime: newTime,
        });
    }

    handleReplaySpeedChange = (speed) => {
        this.setState({ replaySpeed: speed });
    }

    changeReportDate = async e => {
        const { value: cutoffDate } = e.target;
        const { stopTimer = () => {} } = this.props;
        stopTimer();
        this.setState({ cutoffDate }, () => {
            this.loadRaceResults();
        });
    }

    toggleShowOfflineResults = () => {
        const { raceClassContestantStore } = this.props.stores;
        const newValue = !raceClassContestantStore.showOfflineResults;
        raceClassContestantStore.updateKeyValue('showOfflineResults', newValue);
        this.loadRaceResults();
    }

    toggleShowOnlyAutoResults = () => {
        const { raceClassContestantStore } = this.props.stores;
        const newValue = !raceClassContestantStore.showOnlyAutoResults;
        raceClassContestantStore.updateKeyValue('showOnlyAutoResults', newValue);
        this.loadRaceResults();
    }

    loadRaceResults = async () => {
        const { cutoffDate } = this.state;
        const {
            race,
            raceClassId,
        } = this.props;
        const { raceClassContestantStore } = this.props.stores;
        const { showOfflineResults, showOnlyAutoResults } = raceClassContestantStore;

        if (showOfflineResults || showOnlyAutoResults) {
            // Use authenticated endpoint to include offline results or filter by auto results
            await raceClassContestantStore.loadWithResults({
                query: {
                    race: race.id,
                    raceClass: raceClassId,
                    addResults: true,
                    cutoffDate,
                    includeOffline: showOfflineResults ? '1' : '0',
                    onlyAutoResults: showOnlyAutoResults ? '1' : '0',
                }
            });
        } else {
            // Use public endpoint for normal view
            await raceClassContestantStore.loadPublic({
                query: {
                    race: race.id,
                    raceClass: raceClassId,
                    addResults: true,
                    cutoffDate,
                }
            });
        }
    }

    clearCutoffDate = e => {
        this.setState({ cutoffDate: null }, () => {
            this.loadRaceResults();
        });
    }

    componentDidMount() {
        this.calculateTimeUsed();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.raceClassContestants !== this.props.raceClassContestants) {
            this.calculateTimeUsed();
        }
    }

    componentWillUnmount() {
        const { stopTimer = () => {} } = this.props;
        stopTimer();
        this.stopReplayLoop();
    }

    render() {
        const { appState, userStore, raceStore, presenceStore } = this.props.stores;
        const { cutoffDate, replayMode, replayPlaying, replayCurrentTime, replaySpeed, replayStartTime, replayEndTime } = this.state;
        const { raceClassContestantStore } = this.props.stores;
        const { showOfflineResults, showOnlyAutoResults } = raceClassContestantStore;
        const { isDevelopment, path, apiServer } = appState;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { titleText } = this.props;
        const {
            race,
            raceClass,
            classNames,
            autoUpdate,
            autoUpdateCountdown,
            toggleAutoUpdate,
        } = this.props;
        const compactWithSuffix = ms => `${util.isoDateCompact(ms)}${raceLocalSuffix(ms, race, 'isoDateCompact')}`;

        // Use filtered contestants in replay mode, otherwise use props
        const raceClassContestants = this.getFilteredContestants();

        const tdWidth = '45px';
        const sortedCheckpoints = raceClass.checkpoints ? raceClass.checkpoints.sort(fieldSorter(['sortOrder', '-name'])) : [];
        let prevContestant = null;
        let currentPosistion = 0;
        let currentPosistionRaceTime = 0;

        return (<>
            <div class={`w-100 d-flex flex-column ${classNames}`}>
                <div class='w-100 d-flex flex-row justify-content-between align-items-center bg-warning text-dark px-3 py-2 rounded-lg'>
                    <div class='font-weight-bold'>
                        {race.title}: {raceClass.title}
                    </div>
                    <div class='d-flex flex-row align-items-center'>
                        {isAdmin && (
                            <div class='form-check form-switch me-3'>
                                <input
                                    class='form-check-input'
                                    type='checkbox'
                                    id='showOnlyAutoResults'
                                    checked={showOnlyAutoResults}
                                    onChange={this.toggleShowOnlyAutoResults}
                                />
                                <label class='form-check-label small' for='showOnlyAutoResults'>
                                    Only auto
                                </label>
                            </div>
                        )}
                        {(isAdmin || raceStore.isRaceAdmin) && race.addAutoResultsHidden && (
                            <div class='form-check form-switch me-3'>
                                <input
                                    class='form-check-input'
                                    type='checkbox'
                                    id='showOfflineResults'
                                    checked={showOfflineResults}
                                    onChange={this.toggleShowOfflineResults}
                                />
                                <label class='form-check-label small' for='showOfflineResults'>
                                    <Text id='race.show-hidden-auto-results'>Show hidden auto-results</Text>
                                </label>
                            </div>
                        )}
                        {/* Auto-update countdown button */}
                        <button
                            type='button'
                            class={`btn btn-sm ${autoUpdate ? 'btn-dark' : 'btn-outline-dark'} rounded-pill`}
                            onClick={toggleAutoUpdate}
                            title='Toggle auto-update (refreshes every 60 seconds)'
                        >
                            <i class={`fa-solid fa-${autoUpdate ? 'rotate fa-spin' : 'pause'}`} style={autoUpdate ? 'animation-duration: 3s;' : ''} />
                            {autoUpdate && <span class='ms-1' style='font-size: 0.85em;'>{autoUpdateCountdown}s</span>}
                        </button>
                    </div>
                </div>
                {raceClass.warning && <div class='w-100 d-flex flex-column my-1 px-2'>
                    <div class='alert alert-danger' role='alert'>
                        {raceClass.warning}
                    </div>
                </div>}
                {raceClass.sortOrderResults && !raceClass.doNotCountRestTime && <div class='w-100 d-flex flex-column my-1 px-2'>
                    <div class='alert alert-info' role='info'>
                        <Text id='race.result-info-timeused'>RaceClass do not use time equalization. Only race time is counting.</Text>
                    </div>
                </div>}

                {/* Historical view indicator */}
                {replayMode && (
                    <div class='alert alert-info py-1 px-2 mb-1 mx-2'>
                        <i class='fa-solid fa-clock-rotate-left me-2' />
                        <Text id='race.viewing-historical'>Viewing results as of:</Text> {util.isoDate(replayCurrentTime, true)}{raceLocalSuffix(replayCurrentTime, race, 'isoDate', { showSeconds: true })}
                    </div>
                )}

                <div class={`w-100 d-flex px-2 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`} style={replayMode ? 'padding-bottom: 120px;' : ''}>
                    <div class='table-responsive w-100' style='font-size: 0.8em;'>
                        <table class='table table-striped table-hover table-sm align-middle'>
                            <thead>
                                <tr>
                                    {raceClass.checkpoints && raceClass.checkpoints.sort(fieldSorter(['sortOrder', '-name'])).map((cp, idx) => {
                                        const nextCheckpoint = sortedCheckpoints[idx + 1];
                                        if (cp.sortOrder === 1) {
                                            return (<>
                                                <th
                                                    class='p-0 text-center cp-line'
                                                    style={`
                                                        width: ${tdWidth} !important;
                                                        min-width: ${tdWidth} !important;
                                                    `}
                                                    onClick={this.viewRaceClassCheckpoint}
                                                    data-md5={cp.md5}
                                                >
                                                    <Text id='race.result-start'>Start</Text>
                                                </th>
                                                <th class='p-0 font-weight-lighter' style='
                                                    width: 10px !important;
                                                    min-width: 10px !important;
                                                '>{nextCheckpoint?.distanceFromPrev || <>&nbsp;</>}</th>
                                            </>);
                                        }
                                        if (cp.sortOrder === raceClass.checkpoints.length) {
                                            return (<>
                                                <th
                                                    class='p-0 text-center cp-line'
                                                    style={`
                                                        width: ${tdWidth} !important;
                                                        min-width: ${tdWidth} !important;
                                                    `}
                                                    onClick={this.viewRaceClassCheckpoint}
                                                    data-md5={cp.md5}
                                                >
                                                    {raceClass.sortOrderResults && raceClass.doNotCountRestTime ? <>
                                                        <nobr>{cp.name}</nobr>
                                                    </> : <>
                                                        <Text id='race.result-finish'>Finish</Text>
                                                    </>}
                                                </th>
                                                <th class='p-0 font-weight-lighter' style='
                                                    width: 10px !important;
                                                    min-width: 10px !important;
                                                '>{nextCheckpoint?.distanceFromPrev || <>&nbsp;</>}</th>
                                            </>);
                                        }
                                        return (<>
                                            <th
                                                class='p-0 text-center cp-line'
                                                style={`
                                                    width: ${tdWidth} !important;
                                                    min-width: ${tdWidth} !important;
                                                `}
                                                onClick={this.viewRaceClassCheckpoint}
                                                data-md5={cp.md5}
                                            >
                                                {raceClass.sortOrderResults && raceClass.doNotCountRestTime ? <>
                                                    <nobr>{cp.name}</nobr>
                                                </> : <>
                                                    <i class='fa-duotone fa-campground' />
                                                </>}


                                            </th>
                                            <th class='p-0 font-weight-lighter' style='
                                                width: 10px !important;
                                                min-width: 10px !important;
                                            '>{nextCheckpoint?.distanceFromPrev || <>&nbsp;</>}</th>
                                        </>);
                                    })}
                                    <th class='p-0'>Deltaker</th>
                                </tr>
                            </thead>
                            <tbody>
                                {raceClassContestants.filter(e => e.bib > 0).sort(fieldSorter(['hasDns', 'isDNS', 'hasScratched', '-lastCheckpoint', '-lastCheckpointLeft', (raceClass.sortOrderResults || 'lastTimestamp'), 'bib', '-name'])).map((rcc, posIdx) => {
                                    const { id, bib, firstname, lastname, startTime, country, timeused, raceTimeused, totalRaceTime } = rcc;
                                    const cpLength = rcc.lastCheckpoint > 0 ? rcc.lastCheckpoint : 1;
                                    let rccHasScratched = false;
                                    // console.log(rcc)
                                    let firstTimeOut = null;
                                    let firstTimeOutRaw = null;
                                    let timeEqualization = null;
                                    let totalActualRestMinutes = 0
                                    let totalRequiredRestMinutes = 0;
                                    let hasTakenOptionalRest = false;

                                    const prevRcc = { ...prevContestant };
                                    prevContestant = rcc;
                                    return (<>
                                        <tr
                                            class='box-line'
                                            onClick={this.viewRaceClassContestant}
                                            data-id={id}
                                        >
                                            {sortedCheckpoints.slice(0, cpLength).map((cp, idx) => {
                                                const _prevCp = sortedCheckpoints[idx - 1];
                                                const prevRccCp = rcc.checkpoints?.find(e => e.checkpoint === _prevCp?.md5) || {};
                                                const prevHasScratced = prevRccCp.timstampScratch;
                                                const rccCp = rcc.checkpoints?.find(e => e.checkpoint === cp.md5) || {};
                                                if (rccCp.optionalRest) {
                                                    hasTakenOptionalRest = rccCp.optionalRest;
                                                }
                                                const hasLeftCp = rccCp.timestampOut;
                                                const hasScratced = rccCp.timstampScratch;
                                                const isInCp = rccCp.timestampIn && !rccCp.timestampOut;
                                                const isFinished = raceClass.checkpoints.length === idx + 1;
                                                const isTimeEq = cp.timeEqualization == 1 ? true : false;
                                                const isRestRequired = cp.requiredRestMinutes ? true : false;
                                                const isPrologue = cp.isPrologue == 1;
                                                const isStartline = cp.isStartLine == 1;
                                                const isLastCp = cp.sortOrder === raceClass.checkpoints.length - 1;
                                                const timeInCp = !isFinished && rccCp.timestampIn && rccCp.timestampOut ? util.dateDiff(rccCp.timestampIn, rccCp.timestampOut) : 0;

                                                let prevTimeInCp = 0;
                                                let timeIsEqualToPrev = false;
                                                if (isFinished) {
                                                    // rcc.position = posIdx + 1;
                                                    const prevRccCp = prevRcc.checkpoints?.find(e => e.checkpoint === cp.md5) || {};
                                                    prevTimeInCp = prevRccCp.timestampIn;
                                                    timeIsEqualToPrev = prevRccCp.timestampIn === rccCp.timestampIn;
                                                    if (!timeIsEqualToPrev) {
                                                        currentPosistion = posIdx + 1;
                                                        // rcc.position = posIdx;
                                                    }
                                                    if (prevRcc.totalRaceTime !== rcc.totalRaceTime) {
                                                        currentPosistionRaceTime = posIdx + 1;
                                                    }
                                                }
                                                // {prevRcc && <>
                                                //     {prevRcc.lastname} {prevRcc.firstname} {prevRcc.bib}
                                                // </>}

                                                let totRequiredRestForContestant = 0;
                                                let remainingRestMinutes;
                                                if (!isPrologue && !isStartline) {
                                                    totalRequiredRestMinutes += cp.requiredRestMinutes ? parseInt(cp.requiredRestMinutes, 10) : 0;
                                                    totRequiredRestForContestant = totalRequiredRestMinutes + (rcc.timeEqualization?.minutes || 0);
                                                    if (!cp.notPartOfTotalRest) {
                                                        totalActualRestMinutes += timeInCp?.minutes || 0;
                                                    }
                                                    if (cp.useRemainingRest && !hasScratced && isInCp) {
                                                        // useRemainingRest: Calculate remaining mandatory rest at this checkpoint
                                                        remainingRestMinutes = totRequiredRestForContestant - totalActualRestMinutes;
                                                        if (remainingRestMinutes < 0) {
                                                            remainingRestMinutes = 0;
                                                        }
                                                        firstTimeOutRaw = util.isoDate(new Date(rccCp.timestampIn).getTime() + (remainingRestMinutes * 60 * 1000));
                                                        firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (remainingRestMinutes * 60 * 1000));
                                                    } else if (isLastCp && !hasScratced && isInCp) {
                                                        // Last checkpoint without useRemainingRest: use requiredRestMinutes
                                                        remainingRestMinutes = cp.requiredRestMinutes;
                                                        if (remainingRestMinutes < 0) {
                                                            remainingRestMinutes = 0;
                                                        }
                                                        firstTimeOutRaw = util.isoDate(new Date(rccCp.timestampIn).getTime() + (remainingRestMinutes * 60 * 1000));
                                                        firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (remainingRestMinutes * 60 * 1000));
                                                    } else if (!hasScratced && isInCp) {
                                                        if (cp.requiredRestMinutes > 0) {
                                                            firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (cp.requiredRestMinutes * 60 * 1000));
                                                        }
                                                        if (isTimeEq) {
                                                            firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (cp.requiredRestMinutes * 60 * 1000) + (rcc.timeEqualization?.minutes * 60 * 1000));
                                                            timeEqualization = util.secToHms(rcc.timeEqualization?.seconds, true);
                                                        }
                                                        if (rccCp.optionalRest) {
                                                            if (cp.requiredRestMinutesOptional > 0) {
                                                                firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (cp.requiredRestMinutesOptional * 60 * 1000));
                                                            }
                                                            if (cp.timeEqualizationOptional) {
                                                                firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (cp.requiredRestMinutesOptional * 60 * 1000) + (rcc.timeEqualization?.minutes * 60 * 1000));
                                                                timeEqualization = util.secToHms(rcc.timeEqualization?.seconds, true);
                                                            }
                                                        }
                                                    }
                                                }

                                                if (prevHasScratced) {
                                                    return (<>
                                                        {/* <td class={`p-0 text-center`}>
                                                            &nbsp;
                                                        </td> */}
                                                    </>);
                                                }

                                                return (<>
                                                    {cp.sortOrder === 1 ? <>
                                                        <td
                                                            class={`p-0 text-center ${rccCp.positionInToCheckpoint ? 'bg-light text-dark' : ''}`}
                                                        >
                                                            {util.isoTime(startTime)}
                                                        </td>
                                                    </> : <>
                                                        {!hasLeftCp && hasScratced ? <>
                                                            <td
                                                                class={`p-0 text-center bg-danger rounded-pill-right text-white `}
                                                            >
                                                                <div class='d-flex flex-row justify-content-start align-items-center'>
                                                                    {(isInCp || hasLeftCp) && <>
                                                                        <div class='float-left bg-primary px-1' style='width: 30px;'>
                                                                            {raceClass.sortOrderResults ? <>
                                                                                <span class=''>
                                                                                    {rccCp.positionTimeusedOnStage}
                                                                                </span>
                                                                                {/* {raceClass.doNotCountRestTime && <>
                                                                                    <span class='font-weight-lighter text-monospace ms-2 me-2'>{rccCp.timeused}</span>
                                                                                </>} */}
                                                                            </> : <>
                                                                                {rccCp.positionInToCheckpoint}
                                                                            </>}
                                                                        </div>
                                                                    </>}
                                                                    <i class='fa-regular fa-xmark text-white mx-1' />
                                                                </div>
                                                            </td>
                                                        </> : <>
                                                            {isFinished ? <>
                                                                <td
                                                                    class={`p-0 text-center ${rccCp.positionInToCheckpoint ? 'bg-success rounded-pill-right text-white' : ''}`}
                                                                >
                                                                    {raceClass.sortOrderResults && raceClass.doNotCountRestTime ? <>
                                                                        <span class='ms-2'>
                                                                            {rccCp.positionTimeusedOnStage}
                                                                        </span>
                                                                        <span class='font-weight-lighter text-monospace ms-2 me-2'>{rccCp.timeused}</span>
                                                                    </> : <>
                                                                        {rccCp.positionInToCheckpoint && <>
                                                                            {/* {raceClass.sortOrderResults ? posIdx + 1 : rccCp.positionInToCheckpoint} */}
                                                                            {timeIsEqualToPrev ? <>
                                                                                {currentPosistion}
                                                                            </> : <>
                                                                                {posIdx + 1}
                                                                            </>}

                                                                            {/* :: {util.secToHms(totalRaceTime)} :: {rccCp.positionTimeusedOnStage} :: {currentPosistionRaceTime} */}

                                                                            {/* prevTimeInCp: {prevTimeInCp}<br />
                                                                            timeIsEqualToPrev: {timeIsEqualToPrev ? 'true' : 'false'}<br /> */}
                                                                        </>}
                                                                    </>}

                                                                </td>
                                                            </> : <>
                                                                <td
                                                                    class={`p-0 text-center ${rccCp.positionInToCheckpoint ? `${isInCp ? 'bg-success rounded-pill-right' : 'bg-primary'} text-white` : ''}`}
                                                                >
                                                                    {raceClass.sortOrderResults && raceClass.doNotCountRestTime ? <>
                                                                        <span class='ms-2'>
                                                                            {rccCp.positionTimeusedOnStage}
                                                                        </span>
                                                                        <span class='font-weight-lighter text-monospace ms-2'>{rccCp.timeused}</span>
                                                                    </> : <>
                                                                        {rccCp.positionInToCheckpoint}
                                                                    </>}
                                                                </td>
                                                            </>}
                                                        </>}
                                                    </>}

                                                    {/* {hasLeftCp && !isInCp && cp.sortOrder === rcc.lastCheckpointLeft && <> */}
                                                        <td
                                                            class={`p-0 text-center ${hasLeftCp ? `${rcc.hasScratched && hasLeftCp && cp.sortOrder === rcc.lastCheckpointLeft ? 'bg-danger rounded-pill-right' : 'bg-primary'} text-white
                                                                ${hasLeftCp && !isInCp && cp.sortOrder === rcc.lastCheckpointLeft ? 'rounded-pill-right' : ''}
                                                            ` : ''}`}
                                                        >

                                                            {/* positionInToCheckpoint: {rcc.positionInToCheckpoint}<br />
                                                            lastCheckpoint: {rcc.lastCheckpoint}<br />
                                                            lastCheckpointLeft: {rcc.lastCheckpointLeft}<br />
                                                            lastTimestamp: {rcc.lastTimestamp}<br /> */}
                                                            {/* hasLeftCp: {hasLeftCp ? 'true' : 'false'}<br />
                                                            rcc.hasScratched: {rcc.hasScratched ? 'true' : 'false'}<br />
                                                            cp.sortOrder: {cp.sortOrder}<br />
                                                            rcc.lastCheckpointLeft: {rcc.lastCheckpointLeft}<br /> */}
                                                            {hasLeftCp && !isInCp && cp.sortOrder === rcc.lastCheckpointLeft ? <>
                                                                <i class='fa-regular fa-angles-right' />
                                                            </> : <></>}

                                                            {/* {isInCp && !hasScratced && isRestRequired && <>
                                                                firstTimeOut: {firstTimeOut}
                                                                timeEqualization: {rcc.timeEqualization?.seconds}
                                                            </>} */}
                                                            {/* hasScratced: {hasScratced ? 'true' : 'false'}<br /> */}
                                                            {/* sortOrder:"{rccCp.sortOrder}" === "{rcc.lastCheckpointLeft}" */}
                                                            {/* {raceClass.checkpoints.length} === {idx + 1} */}
                                                            {/* isFinished: {isFinished ? 'true' : 'false'} */}

                                                            {isAdmin && isLastCp && !hasScratced && isInCp && <>
                                                                <details onClick={e => e.stopPropagation()} class='text-end'>
                                                                    <summary>
                                                                        <nobr>
                                                                        <i class='fa-solid fa-user-police' />
                                                                        </nobr>
                                                                    </summary>
                                                                    <nobr><span class='font-weight-lighter'>totalRequiredRestMinutes</span>: {totalRequiredRestMinutes}</nobr><br />
                                                                    <nobr><span class='font-weight-lighter'>totRequiredRestForContestant</span>: {totRequiredRestForContestant}</nobr><br />
                                                                    <nobr><span class='font-weight-lighter'>totalActualRestMinutes</span>: {totalActualRestMinutes}</nobr><br />
                                                                    <nobr><span class='font-weight-lighter'>remainingRestMinutes</span>: {remainingRestMinutes}</nobr><br />
                                                                    <nobr><span class='font-weight-lighter'>cp.requiredRestMinutes</span>: {cp.requiredRestMinutes}</nobr><br />
                                                                    <nobr><span class='font-weight-lighter'>remainingRestTime</span>: {util.secToHms(remainingRestMinutes * 60)}</nobr><br />

                                                                    <nobr><span class='font-weight-lighter'>rccCp.timestampIn</span>: {util.isoDate(rccCp.timestampIn)}</nobr><br />
                                                                    <nobr><span class='font-weight-lighter'>firstTimeOutRaw</span>: {firstTimeOutRaw}</nobr><br />
                                                                </details>
                                                            </>}
                                                        </td>
                                                    {/* </>} */}
                                                </>);
                                            })}
                                            <td class={`p-0 `}
                                                colspan='23'
                                            >
                                                <nobr>
                                                    {bib ? <>
                                                        <span class='badge text-bg-dark rounded-pill me-1 ms-0' style={`width: 35px; ${raceClass.bibColor ? `background-color: ${raceClass.bibColor} !important;` : ''}`}>{bib}</span>
                                                    </> : ''} {localUtil.getFlagSvg(country, 15, false, 'rounded')} <span
                                                        style={rcc.user > 0 ? 'cursor: pointer;' : ''}
                                                        onClick={rcc.user > 0 ? (event) => {
                                                            event.stopPropagation();
                                                            openUserProfileDrawer(this.props.stores.appState, {
                                                                userId: rcc.user,
                                                                name: `${firstname} ${lastname}`,
                                                            });
                                                        } : undefined}
                                                    >{util.ucfirst(firstname)} {util.ucfirst(lastname)}{rcc.user > 0 && presenceStore.track(rcc.user)}<PresenceDot online={rcc.user > 0 ? presenceStore.onlineByUserId[rcc.user] : undefined} size={8} inline /></span>
                                                    <JuniorBadge contestant={rcc} />
                                                    {/* {isAdmin && <>
                                                        <small class='text-muted ms-2'>
                                                            (start: {util.isoDate(rcc.startTime)}
                                                            -end: {util.isoDate(rcc.endTime)})
                                                        </small>
                                                    </>} */}

                                                    {rcc.rnb ? <>
                                                        <small><small class='font-weight-lighter ms-2'>RNB1</small></small>
                                                    </> : <></>}
                                                    {rcc.isDNS || rcc.hasDns ? <>
                                                        <span class='text-danger ms-2'><i class='fa-solid fa-times text-danger' /> DNS</span>
                                                    </> : <></>}
                                                    {rcc.hasWarning ? <>
                                                        <i class='fa-solid fa-cards-blank text-warning ms-2' />
                                                    </> : <></>}
                                                    {rcc.hasTimepenalty ? <>
                                                        <i class='fa-solid fa-timer text-warning ms-2' />
                                                    </> : <></>}
                                                    {rcc.isDisqualified ? <>
                                                        <i class='fa-solid fa-user-xmark text-danger ms-2' />
                                                    </> : <></>}
                                                    {rcc.results?.some(r => r.isAutoResult) && (
                                                        <i class='fa-solid fa-satellite-dish text-info ms-2' title='Has auto-detected results' style='font-size: 0.85em;' />
                                                    )}
                                                    {isAdmin && (
                                                        <span
                                                            class='ms-1'
                                                            onClick={(e) => this.openGeofenceDebug(e, rcc)}
                                                            title='Geofence debug (Admin)'
                                                            style='cursor: pointer;'
                                                        >
                                                            <i class='fa-solid fa-bug text-warning' style='font-size: 0.75em;' />
                                                        </span>
                                                    )}
                                                    {/* lastCheckpoint: {rcc.lastCheckpoint} lastCheckpointLeft: {rcc.lastCheckpointLeft} */}
                                                    {/* rccHasScratched: {rccHasScratched ? 'true' : 'false'}<br /> */}

                                                    {raceClass.doNotCountRestTime ? <></> : <>
                                                        {firstTimeOut ? <>
                                                            {/* isTimeEq: {isTimeEq ? 'true' : 'false'} */}
                                                            <span class='font-weight-lighter ms-2'><i class='fa-duotone fa-clock' /> <Text id='race.firsttimeout'>Time out</Text>:</span> {firstTimeOut}
                                                            {/* <span class='font-weight-lighter ms-2'><Text id='race.firsttimeout'>Time eq:</Text></span> {timeEqualization} */}
                                                        </> : <>
                                                            {hasTakenOptionalRest ? <>
                                                                {/* <i class='fa-duotone fa-circle-dot text-success ms-2' /> */}
                                                            </> : <></>}
                                                        </>}
                                                    </>}
                                                </nobr>
                                                <nobr class='float-right'>
                                                    {raceClass.sortOrderResults && <>
                                                        <span class='text-muted ms-2'>
                                                            {raceClass.doNotCountRestTime ? <>
                                                                <span class='font-weight-lighter'><Text id='race.racetime'>Race time</Text>:</span> {util.secToHms(raceTimeused)}
                                                            </> : <>
                                                                <span class='font-weight-lighter'><Text id='race.racetime'>Race time</Text>:</span> {util.secToHms(timeused)}
                                                            </>}
                                                        </span>
                                                    </>}
                                                </nobr>
                                            </td>
                                            {/* <td class='p-0'>
                                                {rcc.lastCheckpoint} /
                                                {rcc.lastCheckpointLeft}
                                            </td> */}
                                        </tr>
                                    </>);
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {raceClassContestants?.some(rcc => rcc.results?.some(r => r.isAutoResult)) && (
                    <div class='text-muted mt-1 px-2' style='font-size: 0.75em;'>
                        <i class='fa-solid fa-satellite-dish text-info me-1' />
                        <Text id='race.auto-result-legend'>= Auto-detected by GPS</Text>
                    </div>
                )}

                <div class='text-muted mt-1 px-2' style='font-size: 0.75em;'>
                    <i class='fa-solid fa-circle-info me-1' />
                    <Text id='race.manual-result-notice'>Resultater registreres manuelt av løpene, kan være forsinket og inneholde feil.</Text>
                </div>

            </div>

            {/* Replay Controls */}
            {replayMode && (
                <ReplayControls
                    isPlaying={replayPlaying}
                    currentTime={replayCurrentTime}
                    startTime={replayStartTime}
                    endTime={replayEndTime}
                    speed={replaySpeed}
                    onPlayPause={this.handleReplayPlayPause}
                    onSeek={this.handleReplaySeek}
                    onSpeedChange={this.handleReplaySpeedChange}
                    onClose={this.toggleReplayMode}
                    stores={this.props.stores}
                />
            )}

            {/* <GuiList
                stores={this.props.stores}
                holdingStore={raceStore}
                classNames={classNames}
                view={this.viewRaceClassContestant}
                title={title}
                list={raceClassContestants}
                elTitle={e => `${e.firstname} ${e.lastname}`}
                elInfo1={e => e.birth ? util.age(e.birth) : ''}
                elInfo2={e => e.sportsClub}
                elInfo3={e => ''}
                hideEmpty={hideEmpty}
                editElement={editElement}
                showEmptyImage={false}
                showCevron={showCevron}
                rightBoxField={rightBoxField}
                rightBoxFieldValue={rightBoxFieldValue}
                rightBoxFieldPostfix={rightBoxFieldPostfix}
                imgHeight='60px'
                help={help}
            /> */}
        </>);
    }
}

export default RaceClassContestantList;
